import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { sensorService } from '../services/sensorService';
import { SensorReading } from '../types';
import { convertTemperature, getStoredTempUnit, tempUnitLabel } from '../utils/temperatureUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TimeScale);

interface WQSPHReadingsChartProps {
  sensorId: number;
  latestReading?: SensorReading | null;
  onNewReading?: (reading: SensorReading) => void;
}

type DateRangePreset = '1d' | '7d' | '30d' | '90d' | 'custom';

const WQSPHReadingsChart: React.FC<WQSPHReadingsChartProps> = ({ sensorId, latestReading, onNewReading }) => {
  const { t } = useTranslation();
  const [readings, setReadings] = useState<SensorReading[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPH, setShowPH] = useState(true);
  const [showTemperature, setShowTemperature] = useState(true);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('1d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastReadingIdRef = useRef<number | null>(null);
  const pollingRef = useRef(false);
  const tempUnit = getStoredTempUnit();

  const getDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();
    if (dateRangePreset === 'custom' && startDate && endDate) {
      return { start: new Date(startDate), end: new Date(endDate) };
    }
    const days = dateRangePreset === '7d' ? 7 : dateRangePreset === '30d' ? 30 : dateRangePreset === '90d' ? 90 : 1;
    start.setDate(start.getDate() - days);
    return { start, end };
  }, [dateRangePreset, startDate, endDate]);

  const loadReadings = useCallback(async (isPolling = false) => {
    if (isPolling && pollingRef.current) return;
    if (isPolling) pollingRef.current = true;
    else setLoading(true);
    try {
      const { start, end } = getDateRange();
      const data = await sensorService.getReadings(sensorId, {
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        limit: 5000,
      });
      const sorted = data.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      setReadings(sorted);
      if (sorted.length > 0) {
        const newest = sorted[sorted.length - 1];
        if (isPolling && lastReadingIdRef.current !== null && newest.id !== lastReadingIdRef.current) onNewReading?.(newest);
        lastReadingIdRef.current = newest.id;
      }
      setError(null);
    } catch (err: any) {
      if (!isPolling) setError(err.response?.data?.detail || t('chart.failedToLoad'));
    } finally {
      if (isPolling) pollingRef.current = false;
      else setLoading(false);
    }
  }, [getDateRange, onNewReading, sensorId, t]);

  useEffect(() => { loadReadings(); }, [loadReadings]);
  useEffect(() => {
    pollingIntervalRef.current = setInterval(() => loadReadings(true), 30000);
    return () => { if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current); };
  }, [loadReadings]);
  useEffect(() => {
    if (!latestReading) return;
    setReadings(current => current.some(r => r.id === latestReading.id)
      ? current
      : [...current, latestReading].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()));
  }, [latestReading]);

  const chartData = {
    labels: readings.map(r => new Date(r.timestamp)),
    datasets: [
      ...(showPH ? [{
        label: t('wqsPh.ph'), data: readings.map(r => r.ph_value ?? null),
        borderColor: 'rgb(8, 145, 178)', backgroundColor: 'rgba(8, 145, 178, 0.4)',
        borderWidth: 2, pointRadius: readings.length > 100 ? 0 : 3, tension: 0.1, yAxisID: 'y',
      }] : []),
      ...(showPH ? [{
        label: t('wqsPh.optimalValue'), data: readings.map(() => 6),
        borderColor: 'rgb(34, 197, 94)', backgroundColor: 'transparent',
        borderWidth: 2, borderDash: [6, 6], pointRadius: 0, tension: 0, yAxisID: 'y',
      }] : []),
      ...(showTemperature ? [{
        label: `${t('wqsPh.probeTemperature')} (${tempUnitLabel(tempUnit)})`,
        data: readings.map(r => r.ph_temperature != null ? convertTemperature(r.ph_temperature, tempUnit) : null),
        borderColor: 'rgb(239, 68, 68)', backgroundColor: 'rgba(239, 68, 68, 0.4)',
        borderWidth: 2, pointRadius: readings.length > 100 ? 0 : 3, tension: 0.1, yAxisID: 'y1',
      }] : []),
      ...(showTemperature ? [{
        label: `${t('wqsPh.waterTemperature')} (${tempUnitLabel(tempUnit)})`,
        data: readings.map(r => r.temp_ds18b20 != null ? convertTemperature(r.temp_ds18b20, tempUnit) : null),
        borderColor: 'rgb(168, 85, 247)', backgroundColor: 'rgba(168, 85, 247, 0.4)',
        borderWidth: 2, pointRadius: readings.length > 100 ? 0 : 3, tension: 0.1, yAxisID: 'y1',
      }] : []),
    ],
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { position: 'top' }, title: { display: true, text: t('wqsPh.chartTitle') } },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: dateRangePreset === '1d' ? 'hour' : dateRangePreset === '90d' ? 'week' : 'day',
          displayFormats: { hour: 'HH:mm', day: 'dd-MM', week: 'dd-MM' },
        },
        title: { display: true, text: t('wqsPh.timeAxis') },
        ticks: {
          autoSkip: true,
          maxTicksLimit: dateRangePreset === '1d' ? 24 : 15,
          minRotation: dateRangePreset === '1d' ? 45 : 0,
          maxRotation: dateRangePreset === '1d' ? 45 : 0,
        },
      },
      y: {
        type: 'linear', display: showPH, position: 'left', min: 0, max: 10,
        title: { display: true, text: t('wqsPh.valueAxis'), color: 'rgb(8, 145, 178)' },
      },
      y1: { type: 'linear', display: showTemperature, position: 'right', title: { display: true, text: `${t('wqsPh.temperature')} (${tempUnitLabel(tempUnit)})`, color: 'rgb(239, 68, 68)' }, grid: { drawOnChartArea: false } },
    },
  };

  const latestTemp = latestReading?.temp_ds18b20 != null ? convertTemperature(latestReading.temp_ds18b20, tempUnit) : null;
  const latestProbeTemp = latestReading?.ph_temperature != null ? convertTemperature(latestReading.ph_temperature, tempUnit) : null;

  return (
    <div className="bg-white shadow rounded-lg p-6">
      {(latestReading?.ph_value != null || latestProbeTemp != null || latestTemp != null) && (
        <div className="mb-4 flex flex-wrap gap-6">
          {latestReading?.ph_value != null && <div><p className="text-xl font-semibold text-cyan-700">{latestReading.ph_value.toFixed(2)}</p><p className="text-xs text-gray-500">{t('wqsPh.ph')}</p></div>}
          {latestProbeTemp != null && <div><p className="text-xl font-semibold text-red-600">{latestProbeTemp.toFixed(1)} {tempUnitLabel(tempUnit)}</p><p className="text-xs text-gray-500">{t('wqsPh.probeTemperature')}</p></div>}
          {latestTemp != null && <div><p className="text-xl font-semibold text-red-600">{latestTemp.toFixed(1)} {tempUnitLabel(tempUnit)}</p><p className="text-xs text-gray-500">{t('wqsPh.waterTemperature')}</p></div>}
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2">
          <button onClick={() => setShowPH(!showPH)} className={`px-3 py-1 text-sm rounded-full border ${showPH ? 'bg-cyan-600 text-white border-cyan-600' : 'text-cyan-700 border-cyan-600'}`}>{t('wqsPh.ph')}</button>
          <button onClick={() => setShowTemperature(!showTemperature)} className={`px-3 py-1 text-sm rounded-full border ${showTemperature ? 'bg-red-500 text-white border-red-500' : 'text-red-600 border-red-500'}`}>{t('wqsPh.temperature')}</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(['1d', '7d', '30d', '90d'] as DateRangePreset[]).map(preset => (
            <button key={preset} onClick={() => { setDateRangePreset(preset); setStartDate(''); setEndDate(''); }} className={`px-3 py-1 text-sm rounded border ${dateRangePreset === preset ? 'bg-primary-600 text-white border-primary-600' : 'text-gray-700 border-gray-300'}`}>
              {preset === '1d' ? t('chart.hours24') : preset === '7d' ? t('chart.days7') : preset === '30d' ? t('chart.days30') : t('chart.days90')}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-4">
        <label className="text-sm text-gray-700">{t('chart.startDate')}<input type="datetime-local" value={startDate} onChange={e => setStartDate(e.target.value)} className="block mt-1 px-3 py-2 border border-gray-300 rounded-md" /></label>
        <label className="text-sm text-gray-700">{t('chart.endDate')}<input type="datetime-local" value={endDate} onChange={e => setEndDate(e.target.value)} className="block mt-1 px-3 py-2 border border-gray-300 rounded-md" /></label>
        <button disabled={!startDate || !endDate} onClick={() => setDateRangePreset('custom')} className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md disabled:opacity-50">{t('chart.applyCustomRange')}</button>
        <button onClick={() => loadReadings()} className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md">{t('chart.refresh')}</button>
      </div>
      <div className="relative h-96">
        {loading ? <div className="absolute inset-0 flex items-center justify-center">{t('chart.loadingReadings')}</div>
          : error ? <div className="absolute inset-0 flex items-center justify-center text-red-600">{error}</div>
          : readings.length === 0 ? <div className="absolute inset-0 flex items-center justify-center text-gray-500">{t('chart.noReadingsForRange')}</div>
          : <Line data={chartData} options={chartOptions} />}
      </div>
    </div>
  );
};

export default WQSPHReadingsChart;
